import Constant from '@/common/constant/Constant';
import translationStore from '@/common/utils/translationStore';

/**
 * tr() - marker dịch cho UI/text cố định.
 *
 * Quy tắc (Pass-through / Fail-safe) - xem docs/specs/multilang/Auto-translate.md:
 *   1. multilingual OFF               -> nguyên văn (原文)
 *   2. mainLanguage == sourceLanguage -> nguyên văn (原文)
 *   3. Cache có bản dịch              -> bản dịch (翻訳)
 *   4. MISS / lỗi                     -> nguyên văn (原文) + ghi nhận MISS
 *
 * Việc gắn tr() KHÔNG BAO GIỜ làm mất chữ đang hiển thị được.
 *
 * Đọc trực tiếp translationStore (Map trong memory) chứ không qua Recoil: tr()
 * được gọi ~65 lần cho mỗi lượt render của HeaderTop + Menu. Recoil chỉ còn giữ
 * một số nguyên (translationVersionAtom) để bắt component re-render khi cache
 * đổi - xem useTranslationSubscription().
 */

const { UI, FIELD_LABEL, TAG } = Constant.TRANSLATION_USAGE;

// Separator between usage and source text inside the miss set, so the flush loop
// can route each miss to the right lookup path.
const MISS_SEPARATOR = String.fromCharCode(0);

// Gom các chuỗi MISS trong phiên để batch gửi Translation Service.
const missSet = new Set();

/** Shared lookup for every synchronous usage. */
const translate = (usage, sourceText) => {
    // Không phải chuỗi hiển thị (null/undefined/empty/số...) -> trả nguyên trạng
    if (typeof sourceText !== 'string' || sourceText.length === 0) {
        return sourceText;
    }

    const { enabled, mainLanguage, sourceLanguage } = translationStore.getConfig();

    if (!enabled) {
        return sourceText; // OFF
    }
    if (mainLanguage === sourceLanguage) {
        return sourceText; // main == source
    }

    const translated = translationStore.get(usage, sourceText);
    if (translated) {
        return translated; // HIT
    }

    // MISS -> ghi nhận để dịch bổ sung, tạm hiển thị nguyên văn
    missSet.add(`${usage}${MISS_SEPARATOR}${sourceText}`);
    return sourceText;
};

/** Chuỗi UI hardcode trong code. Bulk-load sẵn lúc vào app. */
function tr(sourceText) {
    return translate(UI, sourceText);
}

/**
 * Hiển thị nhãn/tag dạng "Bản dịch (Bản gốc)".
 *
 *   'Quality Defect (品質不良)'
 *
 * Khi chưa có bản dịch, hoặc dịch bị tắt / 主言語 == tiếng gốc (translate() trả về
 * chính sourceText), chỉ hiện nguyên văn - KHÔNG kèm ngoặc rỗng.
 */
const withOriginal = (translated, sourceText) => {
    if (!translated || translated === sourceText) {
        return sourceText;
    }
    return `${translated} (${sourceText})`;
};

/** Nhãn form/field/status lấy từ bảng Forms. Bulk-load sẵn lúc vào app. */
export function trLabel(sourceText) {
    return withOriginal(translate(FIELD_LABEL, sourceText), sourceText);
}

/**
 * Tên tag item. KHÔNG bulk-load (org thật có tới 500k tag) - tra lazy theo tag
 * đang hiển thị, nên lần render đầu ra nguyên văn rồi mới đổi.
 */
export function trTag(sourceText) {
    return withOriginal(translate(TAG, sourceText), sourceText);
}

/**
 * @returns {Array<{usage: string, sourceText: string}>} các chuỗi chưa dịch được.
 */
export function getTranslationMisses() {
    return Array.from(missSet).map(entry => {
        const index = entry.indexOf(MISS_SEPARATOR);
        return {
            usage: entry.slice(0, index),
            sourceText: entry.slice(index + 1)
        };
    });
}

export function clearTranslationMisses() {
    missSet.clear();
}

// Debug aid (chỉ dev): mở console gõ __getTranslationMisses() để xem chuỗi MISS.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    window.__getTranslationMisses = getTranslationMisses;
    window.__clearTranslationMisses = clearTranslationMisses;
}

export { tr };
export default tr;
